import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { AttachmentConfigDialogComponent } from '../attachment-config-dialog.component';
import { ValuesModalComponent } from '../../../user-fields-catalog/values-dialog/values-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog.component';
import { FieldConfigInput } from '../../interfaces/field-catalog.interface';
import { fieldTypeOptionsWithouForm, userEntityLabels } from 'app/shared/constants/lists.constant';
import { EntityEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/entity.enum';
import { CompanyFieldCatalogService } from '../../company-form-config.service';
import { IAPIItemResult } from 'app/common/interfaces';
import UserFieldCatalogAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-catalog.entity';
import { UserFieldConfigAttributes } from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-config/user-field-config.entity';
import { take } from 'rxjs';
import { UserFieldEntityTypeEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/user-field-entity-type.enum';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { SelectSchemaComponent } from 'app/shared/components/select-schema/select-schema.component';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'app-subfields-config',
  templateUrl: './subfieldsconfig.component.html',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule,
    MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatTooltipModule, ToggleOptionComponent, SelectSchemaComponent, NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
})
export class SubfieldsConfigComponent implements OnInit {
  form: FormGroup;

  readonly fieldTypeOptions = fieldTypeOptionsWithouForm;

  readonly userEntityList = userEntityLabels;

  userFieldsConfig: UserFieldConfigAttributes[] = [];

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<SubfieldsConfigComponent>,
    private companyFieldCatalogService: CompanyFieldCatalogService,
    private handlerMessage: HandlerMessageService,
    @Inject(MAT_DIALOG_DATA) public data: { subFields: any[]; multiple: boolean; entity: EntityEnum; companyId?: string }
  ) {
    this.form = this.fb.group({
      multiple: [!!this.data.multiple],
      fields: this.fb.array([]),
    });
  }

  ngOnInit(): void {
    if (this.data.subFields?.length > 0) {
      this.form.get('multiple')?.disable();
      this.data.subFields.forEach((f) => this.addField(f));
    }

    setTimeout(() => this.markFormTouched());
  }

  private markFormTouched(): void {
    this.form.markAllAsTouched();

    this.fields.controls.forEach((control) => {
      control.markAllAsTouched();
    });
  }

  positionInGroupValidator(control: AbstractControl) {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;

    const currentGroup = formGroup.get('group')?.value;
    const currentPos = control.value;
    if (!this.fields || currentPos === null || currentPos === '') return null;

    const duplicate = this.fields.controls.find((ctrl) => {
      return (
        ctrl !== formGroup &&
        ctrl.get('group')?.value === currentGroup &&
        ctrl.get('position')?.value == currentPos
      );
    });

    if (duplicate) {
      const groupPositions = this.fields.controls
        .filter((ctrl) => ctrl.get('group')?.value === currentGroup)
        .map((ctrl) => Number(ctrl.get('position')?.value) || 0);

      return {
        duplicatePosition: true,
        lastOccupied: Math.max(...groupPositions),
      };
    }
    return null;
  }

  private revalidateGroupFields() {
    if (!this.fields) return;
    this.fields.controls.forEach((control) => {
      const fg = control as FormGroup;
      fg.get('position')?.updateValueAndValidity({ emitEvent: false, onlySelf: true });
    });
  }

  loadAllUsersFields(userFieldEntity: UserFieldEntityTypeEnum) {
    this.companyFieldCatalogService
      .getFieldsMaster(this.data.companyId, userFieldEntity)
      .pipe(take(1))
      .subscribe({
        next: (res: IAPIItemResult<UserFieldCatalogAttributes>) => {
          const response = res.data;
          this.userFieldsConfig = response.fields || [];
        },
      });
  }

  get fields(): FormArray {
    return this.form.get('fields') as FormArray;
  }

  syncField(field) {
    const selectedFieldName = field.get('userField')?.value;

    const userField = this.userFieldsConfig.find((uf) => uf.fieldName === selectedFieldName);

    if (userField) {
      field.patchValue({
        fieldName: userField.fieldName || '',
        fieldLabel: userField.fieldLabel?.master || '',
        fieldType: userField.fieldType || 'text',
        isVisible: userField.isVisible !== false,
        isTypeMandatory: userField.isTypeMandatory === true,
        isRequired: userField.isRequired === true,
        group: userField.group || null,
        groupPosition: userField.groupPosition || 0,
        icon: userField.icon || null,
        iconColor: userField.iconColor || null,
        position: userField.position || 0,
        maxLength: userField.maxLength ?? null,
        minLength: userField.minLength ?? null,
        caseConversion: userField.caseConversion ?? 'NONE',
        trimSpaces: !!userField.trimSpaces,
        mask: userField.mask ?? '',
        defaultValue: userField.defaultValue ?? (userField.fieldType === 'multiselect' ? [] : ''),
        valuesData: userField.valuesData || [],
        parentFieldName: userField.parentFieldName || '',
        schemaMaster: userField.schemaMaster || '',
        multiple: userField.multiple,
      });
      field.get('fieldName')?.updateValueAndValidity();

      this.handlerMessage.success({ message: 'Datos sincronizados desde el campo de usuario' });
    } else {
      this.handlerMessage.warning({ message: 'No se encontró la configuración del campo seleccionado' });
    }
  }

  addField(data: Partial<FieldConfigInput> = {}): void {
    const fg = this.fb.group({
      fieldName: [{ value: data.name || '', disabled: data.name }, [Validators.required, this.uniqueNameValidator.bind(this)]],
      fieldLabel: [data.label || '', Validators.required],
      fieldType: [{ value: data.type || 'text', disabled: !!data.type }],
      isVisible: [data.isVisible !== false],
      isRequired: [!!data.isRequired],
      isTypeMandatory: [{ value: !!data.isTypeMandatory, disabled: data.isTypeMandatory }],
      position: [data.position || 0, [Validators.required, this.positionInGroupValidator.bind(this)]],
      group: [data.group || null],
      groupPosition: [data.groupPosition || 0],
      icon: [data.icon || null],
      iconColor: [data.iconColor || null],
      maxLength: [data.maxLength ?? null],
      minLength: [data.minLength ?? null, [this.minLengthRangeValidator.bind(this)]],
      caseConversion: [data.caseConversion ?? 'NONE'],
      trimSpaces: [!!data.trimSpaces],
      mask: [data.mask ?? ''],
      defaultValue: [data.defaultValue ?? (data.type === 'multiselect' ? [] : '')],
      allowedExtensions: [data.allowedExtensions || null],
      maxFileSize: [data.maxFileSize || 5],
      multiple: [!!data.multiple],
      valuesData: [data.valuesData ?? []],
      parentFieldName: [data.parentFieldName ?? ''],
      schemaMaster: [{ value: data.schemaMaster ?? '', disabled: !!data.schemaMaster }],
      attachments: [data.attachments ?? []],

      // userFieldsConfig
      isUserField: [{ value: false, disabled: data.name }],
      userFieldEntity: [],
      userField: [null],
    });

    fg.get('group')?.valueChanges.subscribe(() => this.revalidateGroupFields());
    fg.get('position')?.valueChanges.subscribe(() => this.revalidateGroupFields());
    fg.get('maxLength')?.valueChanges.subscribe(() => {
      fg.get('minLength')?.updateValueAndValidity({ emitEvent: false });
    });

    fg.markAllAsTouched();

    this.fields.push(fg);
  }

  // Validador de nombres únicos
  minLengthRangeValidator(control: AbstractControl) {
    const formGroup = control.parent as FormGroup;
    if (!formGroup) return null;
    // El input usa separador de miles, hay que limpiarlo antes de comparar
    const toNumber = (v: any) => (v === null || v === '' ? null : Number(String(v).replace(/,/g, '')));
    const min = toNumber(control.value);
    const max = toNumber(formGroup.get('maxLength')?.value);
    if (min === null || max === null || isNaN(min) || isNaN(max)) return null;
    return min > max ? { minGreaterThanMax: true } : null;
  }

  uniqueNameValidator(control: AbstractControl) {
    const value = control.value?.trim().toLowerCase();
    if (!value || !this.fields) return null;

    const duplicates = this.fields.controls.filter(
      (ctrl) => ctrl.get('fieldName')?.value?.trim().toLowerCase() === value
    );

    return duplicates.length > 1 ? { duplicateName: true } : null;
  }

  // Forzar re-validación de todos los campos cuando uno cambia
  onNameBlur() {
    this.fields.controls.forEach((ctrl) => ctrl.get('fieldName')?.updateValueAndValidity({ emitEvent: false }));
  }

  confirmDelete(index: number) {
    const fieldName = this.fields.at(index).get('fieldLabel')?.value || 'este campo';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmar eliminación',
        message: `¿Estás seguro de que deseas eliminar "${fieldName}"? Esta acción no se puede deshacer.`,
      },
      panelClass: 'responsive-modal',
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.fields.removeAt(index);
        this.onNameBlur(); // Re-validar duplicados tras borrar
      }
    });
  }

  openValuesModal(fieldGroup: FormGroup) {
    const type = fieldGroup.get('fieldType')?.value;

    if (type === 'attachment') {
      this.dialog
        .open(AttachmentConfigDialogComponent, {
          width: '650px',
          panelClass: 'responsive-modal',
          data: {
            attachments: fieldGroup.get('attachments').value || [],
            multiple: fieldGroup.get('multiple').value,
            entity: this.data.entity,
          },
        })
        .afterClosed()
        .subscribe((res) => {
          if (res) {
            fieldGroup.patchValue({
              attachments: res.attachments,
            });
            fieldGroup.patchValue({
              multiple: res.multiple,
            });
          }
        });
      return;
    }

    const dialogRef = this.dialog.open(ValuesModalComponent, {
      width: '700px',
      panelClass: 'responsive-modal',
      data: { values: Array.isArray(fieldGroup.get('valuesData')?.value) ? [...fieldGroup.get('valuesData')?.value] : [] },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) fieldGroup.get('valuesData')?.setValue(res);
    });
  }

  onTypeChanged(fieldGroup: FormGroup) {
    const type = fieldGroup.get('fieldType')?.value;
    fieldGroup.get('defaultValue')?.setValue(['select', 'multiselect'].includes(type) ? [] : type === 'boolean' ? false : '');
  }

  private formatDefaultValue(f) {
    if (f.type === 'multiselect') {
      return Array.isArray(f.defaultValue) ? f.defaultValue : [];
    }
    if (f.type === 'select') {
      return f.defaultValue || null;
    }
    return f.defaultValue;
  }

  save(): void {
    if (this.form.valid) {
      const rawValue = this.form.getRawValue();

      const processedSubFields = rawValue.fields.map((field) => {
        return {
          ...field,
          maxLength: field.maxLength !== null && field.maxLength !== '' ? Number(field.maxLength) : null,
          minLength: field.minLength !== null && field.minLength !== '' ? Number(field.minLength) : null,

          position: field.position !== null && field.position !== '' ? Number(field.position) : 0,
          groupPosition: field.groupPosition !== null && field.groupPosition !== '' ? Number(field.groupPosition) : 0,
          maxFileSize: field.maxFileSize !== null && field.maxFileSize !== '' ? Number(field.maxFileSize) : null,

          defaultValue: this.formatDefaultValue(field),

          name: field.fieldName,
          label: field.fieldLabel,
          type: field.fieldType,
        };
      });

      this.dialogRef.close({
        subFields: processedSubFields,
        multiple: rawValue.multiple,
      });
    }
  }
}
